import { useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useStoreSettings } from './useStoreSettings';
import {
    formatCurrency,
    formatCurrencyDisplay,
    formatCurrencyCompact,
    formatCurrencyWhole,
    getCurrencySymbol,
    getCurrencyShortName,
    parseCurrencyAmount
} from '../utils/currencyUtils';

/**
 * Custom hook that provides currency formatting functions
 * integrated with the current language and store settings
 */
export const useCurrency = () => {
    const { language } = useLanguage();
    const { storeSettings } = useStoreSettings();

    const currencyCode = storeSettings?.currency || 'AED';

    // Standard currency formatting
    const formatAmount = useCallback((amount, options) => {
        return formatCurrency(amount, currencyCode, language, options);
    }, [currencyCode, language]);

    // Display formatting (most common use case)
    const formatDisplay = useCallback((amount) => {
        // console.log('formatDisplay called with:', { amount, currencyCode, language });
        const result = formatCurrencyDisplay(amount, currencyCode, language);
        // console.log('formatDisplay result:', result);
        return result;
    }, [currencyCode, language]);

    // Compact formatting (for small spaces)
    const formatCompact = useCallback((amount) => {
        return formatCurrencyCompact(amount, currencyCode, language);
    }, [currencyCode, language]);

    // Whole number formatting (no decimals)
    const formatWhole = useCallback((amount) => {
        return formatCurrencyWhole(amount, currencyCode, language);
    }, [currencyCode, language]);

    // Get currency symbol for current language
    const getCurrency = useCallback(() => {
        return getCurrencySymbol(currencyCode, language);
    }, [currencyCode, language]);

    // Get currency short name for current language
    const getCurrencyCode = useCallback(() => {
        return getCurrencyShortName(currencyCode, language);
    }, [currencyCode, language]);

    // Legacy support for existing components
    const formatCurrencyLegacy = useCallback((amount) => {
        const currency = getCurrencySymbol(currencyCode, language);
        const formattedAmount = Number(amount || 0).toFixed(2);

        if (language === 'ar') {
            return `${formattedAmount} ${currency}`;
        } else {
            return `${currency} ${formattedAmount}`;
        }
    }, [currencyCode, language]);

    return {
        // Main formatting functions
        formatAmount,
        formatDisplay,
        formatCompact,
        formatWhole,

        // Utility functions
        getCurrency,
        getCurrencyCode,
        parseCurrencyAmount,

        // Context values
        currencyCode,
        language,

        // Legacy support
        formatCurrency: formatCurrencyLegacy,

        // Raw values for components that need them
        symbol: getCurrencySymbol(currencyCode, language),
        shortName: getCurrencyShortName(currencyCode, language)
    };
};

export default useCurrency;
